import { lazy, Suspense, useContext, useEffect, ComponentType } from 'react';
import { Navigate, useNavigate, RouteObject } from 'react-router-dom';

import LoadingScreen from 'components/LoadingScreen';

import LayoutV3 from 'layouts/layout/DashboardLayout';
import axiosInstance from 'utils/axios';
import { baseUrl } from 'utils/constants';
import { clearLocalStorage, localSet } from 'utils/utils';
import ErrorPage from './components/ErrorBoundaryPage';
import { ErrorBoundary } from 'react-error-boundary';
import { SettingsContext } from 'contexts/settingsContext';

const Loadable = (Component: ComponentType) => (props: Record<string, never>) => (
    <Suspense fallback={<LoadingScreen />}>
        <ErrorBoundary FallbackComponent={ErrorPage}>
            <Component {...props} />
        </ErrorBoundary>
    </Suspense>
);
// dashboards
let Merchants!: ReturnType<typeof Loadable>;
const Health = Loadable(lazy(() => import(`./pages/data-table/health`)));
const GiftGuide = Loadable(lazy(() => import(`./pages/dashboard/giftGuide`)));
const ErrorLogs = Loadable(lazy(() => import(`./pages/dashboard/health/logs`)));
const Research = Loadable(lazy(() => import(`./pages/dashboard/research/research`)));
const Inquiries = Loadable(lazy(() => import(`./pages/dashboard/inquiries/inquiries`)));
try {
    Merchants = Loadable(lazy(() => import(`./pages/dashboard/merchants/merchants`)));
} catch (e) {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    window;
}
const Charts = Loadable(lazy(() => import(`./pages/dashboard/charts`)));
const Payouts = Loadable(lazy(() => import(`./pages/dashboard/payouts/payouts`)));
const PersonView = Loadable(lazy(() => import(`./pages/dashboard/research/member`)));
const OffersView = Loadable(lazy(() => import(`./pages/dashboard/offerManagement/offerManagement`)));
const Login = Loadable(lazy(() => import(`./pages/authentication/login`)));
const ForgotPassword = Loadable(lazy(() => import(`./pages/authentication/forget-password`)));
const Reports = Loadable(lazy(() => import(`./pages/dashboard/reports/reports`)));
const FileShares = Loadable(lazy(() => import(`./pages/dashboard/fileShares`)));
const ExternalCoupons = Loadable(lazy(() => import(`./pages/dashboard/externalCoupons/externalCoupons`)));
const Products = Loadable(lazy(() => import(`./pages/dashboard/products`)));
const Error = Loadable(lazy(() => import(`./pages/404`)));

const ActiveLayout = () => {
    const navigate = useNavigate();
    const { checkAndLoadConfig } = useContext(SettingsContext);
    useEffect(() => {
        checkAndLoadConfig();
        if (window.location.pathname !== `/login`) {
            axiosInstance
                .get<Record<string, string>>(`${baseUrl}userinfo`)
                .then(r => {
                    clearLocalStorage();
                    Object.keys(r.data).forEach(i => {
                        localSet(i, r.data[i]);
                    });
                })
                .catch(() => {
                    clearLocalStorage();
                    navigate(`/login`);
                });
        }
    }, [navigate]);

    return <LayoutV3 />;
};

const dashboardRoutes: RouteObject[] = [
    {
        path: `/dashboard/health`,
        element: <Health />,
    },
    {
        path: `/dashboard`,
        element: <Charts />,
    },
    {
        path: `/dashboard/health/errorlogs`,
        element: <ErrorLogs />,
    },
    {
        path: `/dashboard/payouts/:month?/:year?`,
        element: <Payouts />,
    },
    {
        path: `/dashboard/research/:tab?/:search?`,
        element: <Research />,
    },
    {
        path: `/dashboard/inquiries/:network?`,
        element: <Inquiries />,
    },
    {
        path: `/dashboard/giftguide/:tab?`,
        element: <GiftGuide />,
    },
    {
        path: `/dashboard/merchants/:network?`,
        element: <Merchants />,
    },
    {
        path: `/dashboard/member/:numGuid/:tab?/:search?`,
        element: <PersonView />,
    },
    {
        path: `/dashboard/member/:numGuid/:tab/:sid/inquire`,
        element: <PersonView />,
    },
    {
        path: `/dashboard/orders/:numGuid/:tab?/:search?`,
        element: <PersonView />,
    },
    {
        path: `/dashboard/offers/:tab?/:options?`,
        element: <OffersView />,
    },
    {
        path: `/dashboard/reports/:tab?`,
        element: <Reports />,
    },
    {
        path: `/dashboard/fileshares`,
        element: <FileShares />,
    },
    {
        path: `/dashboard/products`,
        element: <Products />,
    },
    {
        path: `/dashboard/externalcoupons`,
        element: <ExternalCoupons />,
    },
];

const authRoutes: RouteObject[] = [
    {
        path: `/`,
        element: <Navigate to="/dashboard/research/members" />,
    },
    {
        path: `login`,
        element: <Login />,
    },

    {
        path: `forget-password`,
        element: <ForgotPassword />,
    },
];

const routes = (): RouteObject[] => {
    return [
        ...authRoutes,
        {
            path: `dashboard`,
            element: <ActiveLayout />,
            children: dashboardRoutes,
        },
        {
            path: `*`,
            element: <Error />,
        },
    ];
};

export default routes;
